import { escapeHtml } from '../util/escapeHtml.js';
import { anchored } from '../util/viewportAnchor.js';
import { statusPhrase } from '../util/statusPhrase.js';
import { mountShell } from '../components/Shell.js';
import { flashToast } from '../util/toast.js';
import { api, HttpError } from '../api/client.js';
import { store } from '../state/store.js';
import { formatBigNum, formatCrowns } from '../util/formatBig.js';
import { cgTable, cgCol, cgRow } from '../util/costReward.js';
import { showLootChanceDialog } from '../components/LootChanceDialog.js';
import type {
  AttackNpcResponse,
  CareerStatsDto,
  CombatRoundDto,
  InventoryItemDto,
  LoadoutDto,
  LoadoutSlotDto,
  NpcDto,
} from '@rex-mortis/shared';
import { EQUIP_PICKER_CSS, slotPickerHtml, wireSlotPickers } from '../util/equipSlots.js';

interface FlashState {
  kind: 'success' | 'danger' | 'warn';
  message: string;
}

interface FightReplay {
  npcName: string;
  outcome: 'WIN' | 'LOSS' | 'DRAW';
  rounds: CombatRoundDto[];
  damageDealt: number;
  damageReceived: number;
  xpGained: number;
  crownsGained: string;
  lootDropped: Array<{ slug: string; name: string; qty: number; tier: string }>;
  hospitalisedUntil: string | null;
  levelsGained: number;
}

/** Build the floating-toast flash for a finished NPC fight — same popup overlay
 *  the Misdeeds / Good Deeds pages use for their outcomes. Head line is the
 *  result + opponent; the tail summarises damage, rewards, loot, and any
 *  hospitalisation, joined with ' · '. The round-by-round replay still renders
 *  inline below; this is the at-a-glance pop-up. */
function fightFlash(f: FightReplay): FlashState {
  const head =
    f.outcome === 'WIN'
      ? `Victory — ${f.npcName}`
      : f.outcome === 'LOSS'
        ? `Defeat — ${f.npcName}`
        : `Draw — ${f.npcName}`;

  const tail: string[] = [`${f.damageDealt} dealt, ${f.damageReceived} taken`, `${f.rounds.length} rounds`];
  if (f.xpGained > 0) tail.push(`+${formatBigNum(f.xpGained)} XP`);
  if (f.crownsGained !== '0') tail.push(`+${formatCrowns(f.crownsGained)} crowns`);
  if (f.levelsGained > 0) tail.push(`🏆 +${f.levelsGained} level${f.levelsGained > 1 ? 's' : ''}!`);
  if (f.lootDropped.length > 0) {
    const mythic = f.lootDropped.some((l) => l.tier === 'mythic');
    tail.push(
      `${mythic ? '👑' : '🎁'} Loot: ${f.lootDropped
        .map((l) => `${l.name}${l.qty > 1 ? ` ×${l.qty}` : ''}`)
        .join(', ')}`,
    );
  }
  if (f.hospitalisedUntil) {
    tail.push(`Hospitalised until ${new Date(f.hospitalisedUntil).toLocaleTimeString()}`);
  }

  const kind: FlashState['kind'] = f.outcome === 'WIN' ? 'success' : f.outcome === 'DRAW' ? 'warn' : 'danger';
  return { kind, message: `${head}\n${tail.join(' · ')}` };
}

/** Caution banner when the player is missing gear — the no-armour case catches
 *  new players who'd otherwise walk in bare and take full damage. */
function gearWarningHtml(loadout: LoadoutDto | null): string {
  if (!loadout) return '';
  const noArmour = loadout.totalArmor <= 0;
  const noWeapon = loadout.totalWeaponAttack <= 0;
  if (!noArmour && !noWeapon) return '';
  const parts: string[] = [];
  if (noArmour) parts.push('no armour equipped — you take <strong>full damage</strong>');
  if (noWeapon) parts.push('no weapon equipped — you fight <strong>bare-fisted</strong>');
  return `<div class="gear-warning">⚔ You have ${parts.join(' and ')}. Equip a weapon, helm, chest, and shield from your <a href="#/inventory">Inventory</a> before you fight.</div>`;
}

const GEAR_WARNING_CSS = `
  .gear-warning {
    background: rgba(212, 169, 60, 0.14);
    border: 1px solid var(--c-gold);
    border-left: 4px solid var(--c-gold);
    border-radius: var(--radius);
    padding: var(--s-2) var(--s-3);
    margin-bottom: var(--s-3);
    color: var(--c-ink);
    font-size: 0.92rem;
    line-height: 1.4;
  }
  .gear-warning a { color: var(--c-oxblood); font-weight: 700; }
`;

export async function renderSparring(root: HTMLElement): Promise<void> {
  let dummies: NpcDto[] = [];
  let loadout: LoadoutDto | null = null;
  let inventory: InventoryItemDto[] = [];
  let lastFight: FightReplay | null = null;
  let flash: FlashState | null = null;
  let careerStats: CareerStatsDto | null = null;

  async function refresh(): Promise<void> {
    try {
      const me = await api.me();
      store.set({ account: me.account, character: me.character });
      dummies = (await api.listDummies()).npcs;
      loadout = (await api.getLoadout()).loadout;
      // Inventory drives the per-slot equip dropdowns.
      try { inventory = (await api.listInventory()).items; } catch { inventory = []; }
      // Combat record for the stats panel — tolerate failure (panel just hides).
      try {
        careerStats = (await api.getCareerStats()).stats;
      } catch {
        careerStats = null;
      }
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : 'Failed to load.';
      flash = { kind: 'danger', message: msg };
    }
  }

  await refresh();

  mountShell(root, (main) => {
    // Live cooldown ticker. Opponent cooldowns are rendered once; without this
    // the "Ready in 30s" text froze and the Attack button only re-enabled on a
    // page reload. Each second we update every .cd-tick span in place, and the
    // moment any cooldown crosses zero we rebuild the opponent list — the stale
    // (now-past) cooldownUntil timestamps then read as ready, so buttons flip
    // back to "Attack" automatically while still honouring vigor/life/status.
    let cdTimer: number | null = null;
    const stopTicker = () => {
      if (cdTimer !== null) {
        clearInterval(cdTimer);
        cdTimer = null;
      }
    };
    function startCooldownTicker(): void {
      stopTicker();
      if (!main.querySelector('.cd-tick[data-cd-until]')) return;
      cdTimer = window.setInterval(() => {
        // Page navigated away: the shell content was swapped out. Bail.
        if (!document.body.contains(main)) {
          stopTicker();
          return;
        }
        const ticks = main.querySelectorAll<HTMLElement>('.cd-tick[data-cd-until]');
        if (ticks.length === 0) {
          stopTicker();
          return;
        }
        let expired = false;
        ticks.forEach((el) => {
          const until = el.dataset.cdUntil!;
          if (new Date(until).getTime() - Date.now() <= 0) expired = true;
          else el.textContent = fmtCooldownRemaining(until);
        });
        // One or more cooldowns elapsed — rebuild so the affected opponents
        // unlock. renderMain() clears this timer and starts a fresh one for any
        // cooldowns still running.
        if (expired) anchored(renderMain);
      }, 1000);
    }

    const renderMain = () => {
      stopTicker();
      // renderMain rebuilds main.innerHTML wholesale, which discards the scroll
      // anchor and flings the page to the top — jarring on mobile when you
      // attack an opponent far down the list, and a visible nudge on desktop.
      // Remember where we were and restore it once the new DOM is in place.
      const savedScrollY = window.scrollY;
      // Remember which opponent sections are expanded — renderMain rebuilds the
      // whole page after every fight, and the player's open accordions should
      // survive that.
      const openSecs = new Set(
        Array.from(main.querySelectorAll<HTMLDetailsElement>('details.opp-section[open]')).map(
          (d) => d.dataset.sec ?? '',
        ),
      );
      const char = store.get().character;
      if (!char || !loadout) {
        main.innerHTML = '<p>Loading…</p>';
        return;
      }

      main.innerHTML = `
        <img class="page-banner" src="/img/banners/sparring.webp" alt="" />
        <h1>The Sparring Hall</h1>
        <p style="font-style: italic; color: var(--c-ink-soft); margin-top: -8px;">
          The dummies do not bleed. Only your pride.
        </p>

        <div id="flash"></div>
        ${gearWarningHtml(loadout)}

        ${recordPanelHtml(careerStats)}

        <section class="panel" id="loadout-panel"></section>

        <h2 style="font-family:var(--font-display); color:var(--c-oxblood); border-bottom:1px solid var(--c-rule); padding-bottom:var(--s-1); margin: var(--s-5) 0 var(--s-3) 0;">Opponents</h2>
        <p style="font-style:italic; color: var(--c-ink-soft);">10 vigor per fight. Loss sends you to the Infirmary. Opponents are matched to your weakest battle stat.</p>
        <div id="dummies"></div>
      `;

      const style = document.createElement('style');
      style.textContent = `
        ${GEAR_WARNING_CSS}
        .loadout-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: var(--s-2);
          margin-top: var(--s-2);
        }
        .slot-card {
          background: var(--c-parchment-dark);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          padding: var(--s-2) var(--s-3);
        }
        .slot-card .label {
          font-family: var(--font-display);
          font-size: 0.85rem;
          color: var(--c-ink-soft);
        }
        .slot-card .value {
          font-family: var(--font-numeric);
          font-size: 1rem;
        }
        .slot-card .stats {
          font-size: 0.8rem;
          color: var(--c-ink-soft);
          margin-top: 2px;
        }
        .slot-card .empty { font-style: italic; color: var(--c-ink-soft); margin-top: 4px; font-size: 0.8rem; }
        ${EQUIP_PICKER_CSS}
        .totals {
          margin-top: var(--s-3);
          padding-top: var(--s-2);
          border-top: 1px solid var(--c-rule-soft);
          display: flex;
          gap: var(--s-4);
          flex-wrap: wrap;
          font-family: var(--font-numeric);
        }
        .dummy-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: var(--s-3);
        }
        .dummy-card {
          background: var(--c-parchment);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          padding: var(--s-3);
          display: flex; flex-direction: column;
        }
        .dummy-card h3 { margin: 0 0 4px 0; font-size: 1.05rem; }
        .dummy-card .blurb {
          font-style: italic;
          color: var(--c-ink-soft);
          font-size: 0.92rem;
          margin-bottom: var(--s-2);
          flex: 1;
        }
        .dummy-card .stats {
          display: flex; flex-wrap: wrap; gap: 6px; font-size: 0.85rem; margin-bottom: var(--s-2);
        }
        .dummy-card .pill {
          background: var(--c-parchment-dark);
          border: 1px solid var(--c-rule-soft);
          border-radius: 999px;
          padding: 1px 8px;
          font-family: var(--font-numeric);
          color: var(--c-ink-soft);
        }
        .dummy-card .pill.warn {
          border-color: var(--c-danger);
          color: var(--c-danger);
        }
        .dummy-card .reward {
          font-size: 0.95rem;
          color: var(--c-gold-text);
          font-weight: 700;
          margin-bottom: var(--s-2);
          font-family: var(--font-numeric);
        }
        /* Per-opponent artwork strip. Hidden until the matching asset exists. */
        .dummy-card .dummy-art {
          display: block;
          width: calc(100% + 2 * var(--s-3));
          margin: calc(-1 * var(--s-3)) calc(-1 * var(--s-3)) var(--s-2);
          height: 140px;
          object-fit: cover;
          border-bottom: 1px solid var(--c-rule);
          border-radius: var(--radius) var(--radius) 0 0;
        }
        /* Combat-record panel — mirrors the Trades "Thy Ledger" stat grid. */
        .record-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
          gap: var(--s-2);
          margin-top: var(--s-2);
        }
        .record-block {
          background: var(--c-parchment-dark);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          padding: var(--s-2) var(--s-3);
        }
        .record-block .label {
          font-family: var(--font-display);
          font-size: 0.8rem;
          color: var(--c-ink-soft);
          letter-spacing: 0.02em;
        }
        .record-block .value {
          font-family: var(--font-numeric);
          font-size: 1.2rem;
          font-weight: 700;
        }
        .record-block.win .value { color: var(--c-success); }
        .record-block.loss .value { color: var(--c-danger); }
        /* Collapsible opponent sections — outgrown foes & distant challenges.
           The player's power bracket renders as full .dummy-card cards above;
           everything else collapses into these compact-row <details> sections. */
        .opp-section {
          margin-bottom: var(--s-2);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          background: var(--c-parchment);
        }
        .opp-band-heading {
          font-family: var(--font-display);
          color: var(--c-ink-soft);
          font-size: 0.85rem;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          margin: var(--s-4) 0 var(--s-2) 0;
          padding-bottom: var(--s-1);
          border-bottom: 1px solid var(--c-rule-soft);
        }
        .opp-section summary {
          cursor: pointer;
          padding: var(--s-2) var(--s-3);
          font-family: var(--font-display);
          color: var(--c-oxblood);
          font-size: 0.95rem;
          letter-spacing: 0.02em;
          list-style: none;
          user-select: none;
        }
        .opp-section summary::-webkit-details-marker { display: none; }
        .opp-section summary::before { content: '\\25B8\\00A0'; color: var(--c-rule); }
        .opp-section[open] summary::before { content: '\\25BE\\00A0'; }
        .opp-section summary:hover { background: rgba(139, 111, 71, 0.10); }
        .opp-rows { display: flex; flex-direction: column; padding: 0 var(--s-2) var(--s-2); }
        .opp-row {
          display: flex;
          align-items: center;
          gap: var(--s-3);
          padding: var(--s-2);
          border-top: 1px solid var(--c-rule-soft);
        }
        .opp-row.locked { opacity: 0.6; }
        .opp-thumb {
          flex: 0 0 auto;
          width: 56px;
          height: 56px;
          object-fit: cover;
          border-radius: var(--radius);
          border: 1px solid var(--c-rule-soft);
        }
        .opp-row-main { flex: 1 1 0; min-width: 0; }
        .opp-row-name { font-family: var(--font-display); font-size: 0.98rem; color: var(--c-ink); }
        .opp-row-meta {
          font-size: 0.8rem;
          color: var(--c-ink-soft);
          font-family: var(--font-numeric);
        }
        .opp-row-reward {
          flex: 0 0 auto;
          text-align: right;
          font-size: 0.82rem;
          font-weight: 700;
          color: var(--c-gold-text);
          font-family: var(--font-numeric);
        }
        .opp-row .btn { flex: 0 0 auto; }
        @media (max-width: 520px) {
          .opp-row-reward { display: none; }
        }
        .replay {
          background: var(--c-parchment);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          padding: var(--s-4);
          margin-bottom: var(--s-4);
        }
        .replay h2 { margin-top: 0; }
        .replay.win { border-color: var(--c-success); }
        .replay.loss { border-color: var(--c-danger); }
        .replay.draw { border-color: var(--c-rule); }
        .replay .round {
          font-family: var(--font-numeric);
          font-size: 0.9rem;
          padding: 4px 8px;
          border-bottom: 1px solid var(--c-rule-soft);
        }
        .replay .round:last-child { border-bottom: 0; }
        .replay .round.attacker-hit { color: var(--c-success); }
        .replay .round.defender-hit { color: var(--c-danger); }
        /* Per-opponent cooldown after a victory. */
        .dummy-card .pill.cooldown { border-color: var(--c-gold); color: var(--c-gold-text); }
        .opp-row.cooldown { opacity: 0.7; }
        /* Post-fight loot — equipment dropped by the defeated opponent. */
        .replay .loot-line { display: flex; flex-wrap: wrap; gap: var(--s-2); margin: var(--s-2) 0; }
        .replay .loot-item {
          background: var(--c-parchment-dark);
          border: 1px solid var(--c-rule);
          border-radius: 999px;
          padding: 2px 10px;
          font-size: 0.88rem;
          font-family: var(--font-numeric);
        }
        .replay .loot-item em { color: var(--c-ink-soft); font-style: normal; text-transform: capitalize; }
        .replay .loot-item.mythic {
          border-color: var(--c-gold);
          color: var(--c-gold-text);
          background: #2a1a08;
          font-weight: 700;
        }
        .replay.mythic {
          border-color: var(--c-gold);
          box-shadow: 0 0 0 2px var(--c-gold) inset, 0 0 28px rgba(212, 160, 23, 0.35);
        }
        .replay .mythic-herald {
          font-family: var(--font-display);
          color: var(--c-gold-text);
          font-size: 1.05rem;
          text-align: center;
          padding: var(--s-2);
          margin-bottom: var(--s-2);
          border-bottom: 1px solid var(--c-gold);
          letter-spacing: 0.03em;
        }
      `;
      main.appendChild(style);

      // Flash → floating toast (overlays in place; no scroll-to-top).
      if (flash) flashToast(flash);

      // Loadout
      const lp = main.querySelector<HTMLDivElement>('#loadout-panel')!;
      const slotCard = (key: 'primary' | 'secondary' | 'helm' | 'chest' | 'amulet', label: string, slot: LoadoutSlotDto): string =>
        `<div class="slot-card"><div class="label">${label}</div>${slotPickerHtml(key, slot, inventory)}</div>`;
      lp.innerHTML = `
        <h2 style="margin-top:0;">Your Loadout</h2>
        <div class="loadout-grid">
          ${slotCard('primary', 'Primary Weapon', loadout.primary)}
          ${slotCard('secondary', 'Off-hand / Shield', loadout.secondary)}
          ${slotCard('helm', 'Helm', loadout.helm)}
          ${slotCard('chest', 'Chest', loadout.chest)}
          ${slotCard('amulet', 'Amulet', loadout.amulet)}
        </div>
        <div class="totals">
          <span>Total weapon attack: <strong>${loadout.totalWeaponAttack || '—'}</strong></span>
          <span>Total accuracy bonus: <strong>${loadout.totalWeaponAccuracy || '—'}</strong></span>
          <span>Total armor: <strong>${loadout.totalArmor || '—'}</strong></span>
        </div>
      `;
      // Per-slot equip pickers — choosing an item equips/unequips, then we
      // refresh the loadout + inventory and re-render with a result toast.
      wireSlotPickers(lp, async (result) => {
        flash = result;
        await refresh();
        anchored(renderMain);
      });

      // The fight result is shown in the floating toast (see fightFlash), the
      // same way Misdeeds / Good Deeds surface their outcomes — no inline result
      // block, so attacking never reflows the page. Full round-by-round detail
      // lives on the Replays page.

      // Opponents — gated by minimum battle stat (lowest of the four), mirroring
      // the Training Yard. With 50 opponents, only the handful around your
      // current power are a real fight, so we carve a BRACKET (your most recent
      // unlocks + your next few goals) and show those as full cards; everything
      // you've outgrown or can't yet reach collapses into compact <details>
      // sections so the page stays scannable.
      const dg = main.querySelector<HTMLDivElement>('#dummies')!;
      const minBattleStat = Math.min(char.strength, char.defense, char.speed, char.dexterity);

      const fightFlags = (
        npc: NpcDto,
      ): { meets: boolean; onCooldown: boolean; canFight: boolean } => {
        const meets = minBattleStat >= npc.unlockMinStat;
        const onCooldown =
          !!npc.cooldownUntil && new Date(npc.cooldownUntil).getTime() > Date.now();
        return {
          meets,
          onCooldown,
          canFight:
            meets && !onCooldown && char.vigor >= 10 && char.life > 0 && char.status === 'OKAY',
        };
      };
      const cardHtml = (npc: NpcDto): string => {
        const { meets, onCooldown, canFight } = fightFlags(npc);
        return `
          <div class="dummy-card">
            <img class="dummy-art" src="/img/opponents/${npc.slug}.webp" alt="" onerror="this.remove()" />
            <h3>${escapeHtml(npc.name)}</h3>
            <div class="blurb">${escapeHtml(npc.blurb)}</div>
            ${cgTable([
              cgCol('Requires', [
                cgRow('All stats', `${formatBigNum(npc.unlockMinStat)}+`, meets ? '' : 'warn'),
                ...(onCooldown ? [cgRow('Cooldown', `⏳ <span class="cd-tick" data-cd-until="${npc.cooldownUntil}">${fmtCooldownRemaining(npc.cooldownUntil!)}</span>`, 'warn')] : []),
              ]),
            ])}
            ${cgTable([
              cgCol('Opponent', [
                cgRow('Strength', formatBigNum(npc.strength)),
                cgRow('Defense', formatBigNum(npc.defense)),
                cgRow('Speed', formatBigNum(npc.speed)),
                cgRow('Dexterity', formatBigNum(npc.dexterity)),
                cgRow('Life', formatBigNum(npc.life)),
              ]),
              cgCol('Reward · on victory', [
                cgRow('XP', `+${formatBigNum(npc.xpOnWin)}`, 'gain'),
                cgRow('Crowns', `+${formatCrowns(npc.crownsOnWin)}`, 'gain'),
                `<div class="cg-row clickable" data-loot="${npc.slug}" role="button" tabindex="0" title="See gear & material odds"><span class="cg-k">Loot</span><span class="cg-v gain">View odds ⓘ</span></div>`,
              ]),
            ])}
            <button class="btn" data-attack="${npc.slug}" ${canFight ? '' : 'disabled'}>
              ${
                !meets
                  ? `Need ${formatBigNum(npc.unlockMinStat)} in every stat`
                  : onCooldown
                    ? `Ready in <span class="cd-tick" data-cd-until="${npc.cooldownUntil}">${fmtCooldownRemaining(npc.cooldownUntil!)}</span>`
                    : char.vigor < 10
                      ? 'Need 10 vigor'
                      : char.life <= 0
                        ? 'Need life'
                        : char.status !== 'OKAY'
                          ? `Cannot while ${statusPhrase(char.status)}`
                          : 'Attack (10 vigor)'
              }
            </button>
          </div>`;
      };
      const rowHtml = (npc: NpcDto): string => {
        const { meets, onCooldown, canFight } = fightFlags(npc);
        let meta = meets
          ? `str ${formatBigNum(npc.strength)} · def ${formatBigNum(npc.defense)} · life ${formatBigNum(npc.life)}`
          : `needs ${formatBigNum(npc.unlockMinStat)} in every battle stat`;
        if (onCooldown) meta = `⏳ ready in <span class="cd-tick" data-cd-until="${npc.cooldownUntil}">${fmtCooldownRemaining(npc.cooldownUntil!)}</span>`;
        return `
          <div class="opp-row ${meets ? '' : 'locked'}${onCooldown ? ' cooldown' : ''}">
            <img class="opp-thumb" src="/img/opponents/${npc.slug}.webp" alt="" onerror="this.remove()" />
            <div class="opp-row-main">
              <div class="opp-row-name">${escapeHtml(npc.name)}</div>
              <div class="opp-row-meta">${meta}</div>
            </div>
            <div class="opp-row-reward">+${formatBigNum(npc.xpOnWin)} XP<br>+${formatCrowns(npc.crownsOnWin)}</div>
            <button class="btn ghost" data-attack="${npc.slug}" ${canFight ? '' : 'disabled'}>
              ${!meets ? 'Locked' : onCooldown ? 'Cooldown' : 'Fight'}
            </button>
          </div>`;
      };

      // Partition ascending by gate → unlocked / locked → carve the bracket.
      const sorted = [...dummies].sort((a, b) => a.unlockMinStat - b.unlockMinStat);
      const unlocked = sorted.filter((n) => minBattleStat >= n.unlockMinStat);
      const locked = sorted.filter((n) => minBattleStat < n.unlockMinStat);
      const BRACKET_UNLOCKED = 6;
      const BRACKET_LOCKED = 3;
      const bracket = [
        ...unlocked.slice(-BRACKET_UNLOCKED),
        ...locked.slice(0, BRACKET_LOCKED),
      ];
      const outgrown = unlocked.slice(0, Math.max(0, unlocked.length - BRACKET_UNLOCKED));
      const distant = locked.slice(BRACKET_LOCKED);

      const sectionHtml = (key: string, label: string, list: NpcDto[]): string => {
        if (list.length === 0) return '';
        return `
          <details class="opp-section" data-sec="${key}" ${openSecs.has(key) ? 'open' : ''}>
            <summary>${label} (${list.length})</summary>
            <div class="opp-rows">${list.map(rowHtml).join('')}</div>
          </details>`;
      };

      // Split the long lists into bite-size sections of 6 so the player can
      // open exactly the band of opponents they care about. Each section is
      // labelled by the opponents it spans.
      const SECTION_SIZE = 6;
      const chunk = (list: NpcDto[]): NpcDto[][] => {
        const out: NpcDto[][] = [];
        for (let i = 0; i < list.length; i += SECTION_SIZE) out.push(list.slice(i, i + SECTION_SIZE));
        return out;
      };
      const spanLabel = (prefix: string, group: NpcDto[]): string => {
        const first = group[0]!;
        const last = group[group.length - 1]!;
        return group.length === 1
          ? `${prefix} · ${escapeHtml(first.name)}`
          : `${prefix} · ${escapeHtml(first.name)} to ${escapeHtml(last.name)}`;
      };

      // Outgrown: strongest first, so the best easy farm sits on top.
      const outgrownSections = chunk([...outgrown].reverse())
        .map((g, i) => sectionHtml(`outgrown-${i}`, spanLabel('Outgrown', g), g))
        .join('');
      // Distant: nearest goals first.
      const distantSections = chunk(distant)
        .map((g, i) => sectionHtml(`distant-${i}`, spanLabel('Train to unlock', g), g))
        .join('');

      dg.innerHTML = `
        <div class="dummy-grid">${bracket.map(cardHtml).join('')}</div>
        ${outgrown.length > 0 ? `<h3 class="opp-band-heading">Opponents you have outgrown — easy crowns & materials</h3>` : ''}
        ${outgrownSections}
        ${distant.length > 0 ? `<h3 class="opp-band-heading">Distant challenges — train to unlock</h3>` : ''}
        ${distantSections}
      `;
      // Loot-odds popup — click the Loot row to open the weighted gear +
      // material drop tables (the same dialog the Misdeeds / Good Deeds pages
      // use). Fetched on demand and cached per opponent for the session.
      const lootCache = new Map<string, import('@rex-mortis/shared').NpcLootResponse>();
      const openLoot = async (slug: string): Promise<void> => {
        try {
          let loot = lootCache.get(slug);
          if (!loot) {
            loot = await api.getNpcLoot(slug);
            lootCache.set(slug, loot);
          }
          showLootChanceDialog({
            title: loot.title,
            dropChance: loot.dropChance,
            dropTable: loot.dropTable,
            dropLabel: 'Gear',
            materials: {
              poolTier: loot.materialPoolTier,
              dropChance: loot.materialDropChance,
              dropTable: loot.materialDropTable,
            },
            ...(loot.mythicNote ? { note: loot.mythicNote } : {}),
          });
        } catch (err) {
          flashToast({ kind: 'danger', message: err instanceof HttpError ? err.message : 'Could not load the loot odds.' });
        }
      };
      dg.querySelectorAll<HTMLElement>('[data-loot]').forEach((row) => {
        const open = (): void => void openLoot(row.dataset.loot!);
        row.addEventListener('click', open);
        row.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            open();
          }
        });
      });

      dg.querySelectorAll<HTMLButtonElement>('button[data-attack]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const slug = btn.dataset.attack!;
          dg.querySelectorAll<HTMLButtonElement>('button[data-attack]').forEach((b) => (b.disabled = true));
          try {
            const r: AttackNpcResponse = await api.attackNpc({ npcSlug: slug });
            store.set({ character: r.character });
            lastFight = {
              npcName: r.npcName,
              outcome: r.outcome,
              rounds: r.rounds,
              damageDealt: r.damageDealt,
              damageReceived: r.damageReceived,
              xpGained: r.xpGained,
              crownsGained: r.crownsGained,
              lootDropped: r.lootDropped,
              hospitalisedUntil: r.hospitalisedUntil,
              levelsGained: r.levelsGained,
            };
            // Pop the fight summary in the same floating overlay Misdeeds /
            // Good Deeds use. The round-by-round replay still renders inline.
            flash = fightFlash(lastFight);
            await refresh();
          } catch (err) {
            const msg = err instanceof HttpError ? err.message : 'Attack failed.';
            flash = { kind: 'danger', message: msg };
          } finally {
            anchored(renderMain);
          }
        });
      });

      // Begin the live countdown for any opponents still on cooldown.
      startCooldownTicker();

      // Restore the pre-rebuild scroll position so attacking (or a cooldown
      // tick rebuild) leaves you where you were instead of at the top.
      window.scrollTo(0, savedScrollY);
    };

    anchored(renderMain);
  });
}

/**
 * Combat-record panel for the top of the Sparring Hall — mirrors the Trades
 * "Thy Ledger" stat-grid pattern. Counts every fight the player has had
 * (Sparring dummies + PvP), since the career stats don't separate them.
 * Returns '' when stats aren't loaded so the page still renders.
 */
function recordPanelHtml(stats: CareerStatsDto | null): string {
  if (!stats) return '';
  const c = stats.combat;
  const decisive = c.wins + c.losses + c.draws;
  const winRate = decisive > 0 ? Math.round((c.wins / decisive) * 100) : 0;
  const block = (label: string, value: string | number, klass = ''): string =>
    `<div class="record-block ${klass}">
       <div class="label">${label}</div>
       <div class="value">${value}</div>
     </div>`;
  return `
    <section class="panel" id="record-panel">
      <h2 style="margin-top:0;">Thy Record</h2>
      <div class="record-grid">
        ${block('Victories', formatBigNum(c.wins), 'win')}
        ${block('Defeats', formatBigNum(c.losses), 'loss')}
        ${block('Draws', formatBigNum(c.draws))}
        ${block('Escaped', formatBigNum(c.escaped))}
        ${block('Win rate', `${winRate}%`)}
        ${block('Total fights', formatBigNum(c.fightsInitiated))}
        ${block('Damage dealt', formatBigNum(c.totalDamageDealt))}
        ${block('Damage taken', formatBigNum(c.totalDamageReceived))}
        ${block('XP from combat', formatBigNum(c.xpFromCombat))}
      </div>
    </section>
  `;
}

/** Relative cooldown remaining from an ISO timestamp: "3h 12m", "8m", "45s". */
function fmtCooldownRemaining(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'ready';
  const totalSec = Math.ceil(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

// (formatBigNum imported at the top of this file from ../util/formatBig.js)

